import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Pagination } from "antd";
import moment from "moment";

import { ShortlistAction, SidebarCountAction } from "../../store/actions";

import useScreenSize from "../../utils/usescreensize";

import ProfileTab from "../../components/tab/profiletab";
import DashboardCard from "../../components/dashboardcard/dashboardcard";
import { WhitePurpleButton } from "../../components/button";
import SortDropdown from "../../components/dropdown/sortdropdown";
import Cardloader from "../../components/loader/cardloader";
import Spacer from "../../components/spacer/spacer";
import UnderHeaderMenu from "../../components/underheadermenu";

import images from "../../assets";

import "./styles.css";

const Shortlists = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screenSize = useScreenSize();
  const [searchParams, setSearchParams] = useSearchParams();

  const validTabs = ["Shortlisted Me", "Shortlisted by Me"];
  const defaultTab = validTabs.includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "Shortlisted Me";
  const defaultPage = parseInt(searchParams.get("page")) || 1;

  const { shortlists, isLoading } = useSelector((state) => state.shortlists);

  const [state, setState] = useState({
    activeTab: defaultTab,
    sortOption: "desc",
  });
  const [pageNo, setPageNo] = useState(defaultPage);
  const [pageSize] = useState(8);

  useEffect(() => {
    dispatch(SidebarCountAction.updateSidebarCount({ table: "shortlist" }));
  }, []);

  useEffect(() => {
    fetchShortlists();
  }, [state.activeTab, state.sortOption]);

  useEffect(() => {
    setSearchParams({ tab: state.activeTab, page: pageNo.toString() });
  }, [state.activeTab, pageNo]);

  useEffect(() => {
    const tabFromURL = searchParams.get("tab") || "Shortlisted Me";
    const pageFromURL = parseInt(searchParams.get("page")) || 1;

    if (tabFromURL !== state.activeTab) {
      setState((prev) => ({ ...prev, activeTab: tabFromURL }));
    }

    if (pageFromURL !== pageNo) {
      setPageNo(pageFromURL);
    }
  }, [searchParams]);

  const fetchShortlists = () => {
    const payload = {
      filter: state?.activeTab,
      sort: state?.sortOption,
    };
    dispatch(ShortlistAction.getShortlist(payload));
  };

  const tabItems = [
    { name: "Shortlisted Me", title: "Shortlisted Me" },
    { name: "Shortlisted by Me", title: "Shortlisted by Me" },
  ];

  const sortedShortlists = useMemo(() => {
    if (!shortlists) return [];
    let sortedData = [...shortlists];

    if (state.sortOption === "desc") {
      sortedData.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      sortedData.sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    }

    return sortedData;
  }, [shortlists, state.sortOption]);

  const paginatedShortlists = useMemo(() => {
    return sortedShortlists.slice((pageNo - 1) * pageSize, pageNo * pageSize);
  }, [sortedShortlists, pageNo, pageSize]);

  return (
    <div>
      {screenSize.width < 1023 && (
        <UnderHeaderMenu titlename="Shortlists">
          <div style={{ width: "110px" }}>
            <SortDropdown
              data={[
                { label: "Newest First", value: "desc" },
                { label: "Oldest First", value: "asc" },
              ]}
              onChange={(e) => setState({ ...state, sortOption: e })}
              value={state?.sortOption || undefined}
              placeholder="Sort by"
            />
          </div>
        </UnderHeaderMenu>
      )}
      <Spacer height="16px" />
      <div className="shortlist-section-top-part-container">
        <div></div>
        <div className="shortlist-section-header-container">
          {screenSize.width > 1023 && (
            <h1 className="shortlist-header">Shortlists</h1>
          )}
          <ProfileTab
            tabItems={tabItems}
            handleTab={(name) => {
              setState((prev) => ({ ...prev, activeTab: name }));
              setPageNo(1);
            }}
            activeTab={state?.activeTab}
            style={{
              background: "white",
              border: "1px solid #EAE6ED",
              width: "250px",
            }}
          />
        </div>
        {screenSize.width > 1023 && (
          <div className="shortlist-sort-container">
            <div style={{ width: "150px" }}>
              <SortDropdown
                data={[
                  { label: "Newest First", value: "desc" },
                  { label: "Oldest First", value: "asc" },
                ]}
                onChange={(e) => setState({ ...state, sortOption: e })}
                value={state?.sortOption || undefined}
                placeholder="Sort by"
              />
            </div>
          </div>
        )}
      </div>
      <Spacer height="12px" />
      <div className="shortlist-results-container">
        {isLoading ? (
          <Cardloader />
        ) : paginatedShortlists.length > 0 ? (
          <>
            <div className="shortlist-results">
              {paginatedShortlists.map((item, index) => (
                <DashboardCard
                  item={item}
                  index={index}
                  key={index}
                  redirectedfrom="shortlists"
                >
                  <div className="flex flex-col w-full gap-3">
                    {state?.activeTab === "Shortlisted Me" && (
                      <div className="shortlist-card-text">
                        <p>Shortlisted me on:</p>
                        <p>{moment(item?.updatedAt).fromNow()}</p>
                      </div>
                    )}
                    {state?.activeTab === "Shortlisted by Me" && (
                      <div className="shortlist-card-text">
                        <p>Shortlisted on:</p>
                        <p>{moment(item?.updatedAt).fromNow()}</p>
                      </div>
                    )}

                    <WhitePurpleButton
                      text="View Profile"
                      onClick={() =>
                        navigate(`/view-profile/${item?.username}`)
                      }
                    />
                  </div>
                </DashboardCard>
              ))}
            </div>
            <Spacer height="36px" />
            <div className="flex justify-center items-center">
              <Pagination
                pageSize={pageSize}
                total={sortedShortlists.length}
                current={pageNo}
                showSizeChanger={false}
                onChange={(page) => setPageNo(page)}
              />
            </div>
            <Spacer height="24px" />
          </>
        ) : (
          <div className="flex flex-col justify-center items-center p-10 w-full gap-4">
            <img
              src={images.noActivityIcon}
              alt="no notifications"
              className="h-[160px] lg:h-full"
            />
            <div className="text-basePrimary font-bold text-[24px] lg:text-[32px]">
              {state?.activeTab === "Shortlisted Me"
                ? "No One Shortlisted You Yet"
                : "No One Has Access Yet"}
            </div>
            <p className="text-[14px] text-center max-w-[500px]">
              {state?.activeTab === "Shortlisted Me"
                ? "Once someone shortlists you, it’ll show up here."
                : "Found a profile you like? Add them to your shortlist."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shortlists;
